import { action } from '@ember/object';
import { once } from '@ember/runloop';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { DateRangeInterface, DSSetup, Service } from 'webapp-types';
import { GranulationTypeId, ScriptDetailItemType } from 'webapp/enums/enums';
import { copyObject, equal, generateHash, isAlive } from 'webapp/utils/common';
import { escapeHtml } from 'webapp/utils/html';
import { hashCode } from 'webapp/utils/number';

import type DataSelect from 'webapp/models-custom/data-select';
import DateRange from 'webapp/models-custom/date-range';
import MetricSetup from 'webapp/models-custom/metric-setup';
import type {
	ExpressionDetailItem,
	ExpressionDetailItemFunction,
} from 'webapp/models-custom/script';
import Serviced from 'webapp/models-custom/serviced';

export const enum ScriptItemGeneralType {
	CONSTANT = ScriptDetailItemType.CONSTANT,
	METRIC = ScriptDetailItemType.METRIC,
	PLACEHOLDER = 'placeholder',
	MISSING = 'missing',
}

type ScriptItemType = ScriptItemGeneralType | string; // fallback for math signs

type BracketItemsForOperator = [ScriptItem, ScriptItem];

interface ExpressionDetailItemFunctionWithMetricSetup extends ExpressionDetailItemFunction {
	metricSetup?: DSSetup.MetricSetupData;
}

export default class ScriptItem extends Serviced {
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;
	@service('model-manager/scripts') declare mmScriptsService: Service.ModelManager.Scripts;

	@tracked autoOpen = false;
	@tracked isShownInList = false;
	@tracked isBracketSelected = false;

	@tracked dataSelect: DataSelect | null = null;

	@tracked previewString = ''; // the result returned from BE

	// valueHash, but the previous value - the one that was queried already
	// used in checking if the item has changed (to keep previewString)
	queriedValueHash = 0;

	@tracked value: ExpressionDetailItem | string = ''; // set in setInitialValue

	constructor(
		type: ScriptItemType,
		value: string | ExpressionDetailItem = '',
		isShownInList = false,
	) {
		super();
		this._type = type;
		this.isShownInList = isShownInList;
		this.setInitialValue(value);
	}

	@tracked private _type: ScriptItemType = '';

	get id() {
		const value = this.value;
		if (typeof value === 'string') {
			return null;
		} else {
			return value.id;
		}
	}

	get type() {
		return this._type;
	}

	set type(newType: ScriptItemType) {
		if (this._type === newType) {
			return;
		}
		this._type = newType;
		if (this.isBracket || this.isMath) {
			this.value = newType;
		} else if (this.isConst || this.isMetric) {
			const value = this.value;
			let id = typeof value === 'string' ? '' : value.id;
			if (!id) {
				id = generateHash(10);
			}
			const expressionDetails: ExpressionDetailItem = {
				id,
				type: newType as ScriptDetailItemType,
			};
			this.value = expressionDetails;
		}
	}

	get constValue(): number | null {
		if (this.isConst) {
			return (this.value as ExpressionDetailItem).value!;
		}
		return null;
	}

	set constValue(value: string) {
		if (this.isConst) {
			(this.value as ExpressionDetailItem).value = parseFloat(value) || 0;
			this.value = this.value;
		}
	}

	get isConst() {
		return this.type === ScriptItemGeneralType.CONSTANT;
	}

	get isMk() {
		return this.type === ScriptItemGeneralType.METRIC;
	}

	get isPlaceholder() {
		return this.type === ScriptItemGeneralType.PLACEHOLDER;
	}

	get isMissing() {
		return this.type === ScriptItemGeneralType.MISSING;
	}

	get isMetric() {
		return this.isConst || this.isMk || this.isPlaceholder || this.isMissing;
	}

	get isMath() {
		return ['+', '-', '*', '/'].includes(this.type);
	}

	get isBracket() {
		return ['(', ')'].includes(this.type);
	}

	get mathType() {
		switch (this.type) {
			case '+':
				return 'addition';
			case '-':
				return 'subtraction';
			case '*':
				return 'multiplication';
			case '/':
				return 'division';
			default:
				return null;
		}
	}

	get mathIconName() {
		switch (this.type) {
			case '+':
				return 'CalculationPlus';
			case '-':
				return 'CalculationMinus';
			case '*':
				return 'CalculationMultiply';
			case '/':
				return 'CalculationDivide';
			default:
				return null;
		}
	}

	@cached
	get listItemValue() {
		let listItemValue = '';

		if (this.isShownInList) {
			if (this.isMath) {
				listItemValue = `<span class="sign"><ui-icon name="${this.mathIconName}" size="sm"></ui-icon></span>`;
			} else if (this.isConst) {
				listItemValue = escapeHtml(String((this.value as ExpressionDetailItem)?.value));
			} else {
				listItemValue = this.value as string;
			}
		}

		return listItemValue;
	}

	get previewIsLoading() {
		return this.previewString === '';
	}

	@action
	scheduleUpdateValue() {
		once(this, this.updateValue);
	}

	private updateValue() {
		if (this.isMk) {
			const expressionDetail = this.value as ExpressionDetailItem,
				dsDataOld = this.getDataSelectPropertiesFromValue(expressionDetail),
				dsData = this.getUserSpecifiedDataSelectProperties();

			if (!equal(dsDataOld, dsData, true)) {
				if (!equal(expressionDetail.metricSetup, dsData.metricSetup)) {
					expressionDetail.metricSetup = dsData.metricSetup;
				}
				delete dsData.metricSetup;
				if (Object.keys(dsData).length) {
					if (!equal(expressionDetail.function, dsData, true)) {
						expressionDetail.function = dsData;
					}
				} else {
					expressionDetail.function = undefined;
				}
				this.value = expressionDetail;
			}
		}
	}

	getOutputInCode() {
		let value = '';

		if (this.isShownInList) {
			value = this.listItemValue;
		} else {
			if (this.isPlaceholder) {
				value = '';
			} else if (typeof this.value === 'string') {
				value = this.value;
			} else {
				value = `{${this.value.id}}`;
			}
		}
		return value;
	}

	get outputInUI() {
		const value = this.value;
		if (typeof value === 'string') {
			return value;
		} else if (this.isConst) {
			return value.value;
		} else {
			return value.id;
		}
	}

	get valueHash() {
		return hashCode(
			JSON.stringify({
				value: this.value,
			}),
		);
	}

	getExpressionDetails() {
		return typeof this.value === 'string' ? null : this.value;
	}

	private setInitialValue(value: string | ExpressionDetailItem) {
		if (this.isMath || this.isBracket) {
			this.value = this.type;
		} else {
			if (this.isConst || this.isPlaceholder) {
				this.value = value;

				if (!this.isShownInList) {
					this.setDataSelect(); // empty, so we can switch later
				}
			} else {
				const expression = value as ExpressionDetailItem;
				const dsProps = this.getDataSelectPropertiesFromValue(expression);
				const metricSetupData = dsProps.metricSetup!;

				const metricSetup = new MetricSetup(metricSetupData);

				const metric = metricSetup.metric;

				if (this.isShownInList) {
					// value always string, for getOutputInCode
					this.value = metric
						? escapeHtml(metricSetup.selectedMetricTitle)
						: '<span class="sign sign-missing" title="Deleted metric"><ui-icon name="Trash" size="sm"></ui-icon></span>';
				} else {
					const source = metric?.source;

					if (!metric || !source) {
						// key not in store or missing datasource
						this.type = ScriptItemGeneralType.MISSING;

						this.setDataSelect();
					} else {
						this.setDataSelect(metricSetupData);
						delete dsProps.metricSetup; // to avoid resetting attribute/unit as result of metric change

						// is static if dateRange is specified or just All Time available
						const dataSelect = this.dataSelect;
						if (dataSelect) {
							Object.keys(dsProps).forEach((key) => {
								const typedKey = key as keyof ExpressionDetailItemFunction;
								if (dsProps[typedKey]) {
									dataSelect[typedKey] = dsProps[typedKey];
								}
							});
							dataSelect.isScriptStatic = !!dsProps.dateRange;
						}
					}

					this.value = expression;
				}
			}
		}
	}

	bracketChange(items: ScriptItem[], isSelect: boolean) {
		// called on an operator item
		const start = this.getLeftsideItemStartIndex(items)!;

		if (isSelect) {
			const end = this.getRightsideItemEndIndex(items)!;
			items.splice(end + 1, 0, new ScriptItem(')'));
			items.splice(start, 0, new ScriptItem('('));
		} else {
			let indexToRemove = this.getRightsideBracketIndex(items),
				rem = indexToRemove ? items[indexToRemove] : null;
			if (indexToRemove && rem) {
				items.splice(indexToRemove, 1);
				rem.cleanup();
			}

			indexToRemove = start - 1;
			rem = items[indexToRemove];
			if (rem) {
				items.splice(indexToRemove, 1);
				rem.cleanup();
			}
		}

		this.isBracketSelected = isSelect;
	}

	updateDateRangeFromFormula(formula: DateRangeInterface.FormulaInterface) {
		// being careful - we should not overwrite user-set props
		const dataSelect = this.dataSelect;

		if (!dataSelect || !isAlive(dataSelect)) {
			return;
		}

		const dsData = this.getUserSpecifiedDataSelectProperties();

		if (!dsData.dateRange && !dataSelect.dateRangeCM.isEqual(formula)) {
			// only range - granulation should stay Total
			dataSelect.updateDateRange(DateRange.createFromFormula(formula), true);
		}
	}

	getLeftsideItemStartIndex(items: ScriptItem[]) {
		// only called on operators (math/brackets)

		const itemIndex = items.indexOf(this);

		let prevItem = items[itemIndex - 1],
			bracketCounter = 0,
			prevGroupIndex = null;

		if (prevItem) {
			// if previous item is a closing bracket, previous group starts with the matching starting bracket
			if (prevItem.type === ')') {
				let i = itemIndex - 2;
				while (i >= 0) {
					prevItem = items[i];
					if (prevItem?.type === ')') {
						bracketCounter--; // we need to be on 0 when we find the opening bracket
					} else if (prevItem?.type === '(') {
						if (!bracketCounter) {
							prevGroupIndex = i;
							break;
						}
						bracketCounter++;
					}
					i--;
				}
			} else {
				// this IS the previous item
				prevGroupIndex = items.indexOf(prevItem);
			}
		}

		return prevGroupIndex;
	}

	getRightsideItemEndIndex(items: ScriptItem[]) {
		// only called on operators (math/brackets)

		const itemIndex = items.indexOf(this);

		let nextItem = items[itemIndex + 1],
			nextGroupIndex = null;

		if (nextItem) {
			// if next item is an opening bracket, next group ends with the matching ending bracket
			if (nextItem.type === '(') {
				let bracketCounter = 0; // we need to be on 0 when we find the closing bracket
				let i = itemIndex + 2;
				while (i < items.length) {
					nextItem = items[i];
					if (nextItem?.type === '(') {
						bracketCounter++;
					} else if (nextItem?.type === ')') {
						if (!bracketCounter) {
							nextGroupIndex = i;
							break;
						}
						bracketCounter--;
					}
					i++;
				}
			} else {
				// this IS the next item
				nextGroupIndex = items.indexOf(nextItem);
			}
		}

		return nextGroupIndex;
	}

	getRightsideBracketIndex(items: ScriptItem[]) {
		// only called on operators (math/brackets)

		let endIndex: number | null = null;
		// eslint-disable-next-line @typescript-eslint/no-this-alias
		let endItem: ScriptItem = this;
		do {
			const index = endItem.getRightsideItemEndIndex(items);
			if (index) {
				endItem = items[index]!;
				if (items[index + 1]?.type === ')') {
					endIndex = index + 1;
				}
			} else {
				break;
			}
		} while (endIndex === null);

		return endIndex;
	}

	private getDataSelectPropertiesFromValue(
		expressionDetails: ExpressionDetailItem,
	): ExpressionDetailItemFunctionWithMetricSetup {
		if (this.isMk) {
			return Object.assign({}, expressionDetails.function || {}, {
				metricSetup: expressionDetails.metricSetup
					? copyObject(expressionDetails.metricSetup)
					: undefined,
			});
		}
		return {};
	}

	private getUserSpecifiedDataSelectProperties(): ExpressionDetailItemFunctionWithMetricSetup {
		const dataSelect = this.dataSelect;

		if (!dataSelect) {
			return {};
		}

		const dsData: ExpressionDetailItemFunctionWithMetricSetup = {};

		// ONLY send daterange when static
		if (dataSelect.isScriptStatic) {
			dsData.dateRange = copyObject(dataSelect.dateRange);
		}

		if (dataSelect.metricSetupKey) {
			dsData.metricSetup = dataSelect.serializedMetricSetupData!;
		}

		return dsData;
	}

	private setDataSelect(metricSetupData?: DSSetup.MetricSetupData) {
		const dataSelect = this.mmDataSelectsService.createNewModel({
			id: this.mmScriptsService.generateTemporaryFakeId(),
			metricSetupKey: metricSetupData?.key,
			metricSetupSpaceAccessId: metricSetupData?.spaceAccessId,
			metricSetupDimensions: metricSetupData?.dimensions || [],
			metricSetupFilters: metricSetupData?.filters,
			dateRange: {
				range: {}, // filled from the script's date range (scripts/form/code.gts), or set when static
				granulation: DateRange.getPredefinedGranulationFormula(GranulationTypeId.TOTAL),
			},
		});
		this.dataSelect = dataSelect;
	}

	cleanup() {
		if (this.dataSelect) {
			// remove the metricsettings created
			const model = this.dataSelect.model;
			model.unloadRecord();
		}
	}

	static setSelectedBrackets(items: ScriptItem[]) {
		// set 'isBracketSelected' for every item.
		// for cases where we have more than 1 operator in the brackets, the bracket is set to true on first one

		items.forEach((item) => {
			const bracketItems = this.getBracketItemsForOperator(item, items);
			item.isBracketSelected = bracketItems !== null;
		});
	}

	static getBracketItemsForOperator(
		item: ScriptItem,
		items: ScriptItem[],
	): BracketItemsForOperator | null {
		// only called on operators
		if (!item.isMath) {
			return null;
		}

		// for cases where we have more than 1 operator in the brackets,
		// the bracket is set to true on first one (bcos of how getLeftsideItemStartIndex works)
		const prevIndex = item.getLeftsideItemStartIndex(items),
			endBracketIndex = item.getRightsideBracketIndex(items);
		if (typeof prevIndex === 'number' && typeof endBracketIndex === 'number') {
			const bracketStartItem = items[prevIndex - 1],
				brackedEndItem = items[endBracketIndex];
			if (bracketStartItem?.type === '(' && brackedEndItem?.type === ')') {
				return [bracketStartItem, brackedEndItem];
			}
		}
		return null;
	}

	static cleanupItems(items: ScriptItem[]) {
		let isChanged;

		let cleanedItems = Array.from(items);

		// remove all placeholders!
		cleanedItems = cleanedItems.filter((item) => !item.isPlaceholder);

		// must follow the pattern "metric" | "bracket/operator" | "metric" etc.
		// loop it until whole round from start to end hasn't returned any inconsistencies
		do {
			isChanged = false;

			// remove excess brackets (by count)
			const bracketCount =
				cleanedItems.filter((item) => item.type === '(').length -
				cleanedItems.filter((item) => item.type === ')').length;
			if (bracketCount !== 0) {
				isChanged = true;
				const bracketsToDelete = bracketCount > 0 ? '(' : ')';
				for (let i = 0; i < Math.abs(bracketCount); i++) {
					const itemToRemove = cleanedItems.find((i) => i.type === bracketsToDelete);
					if (itemToRemove) {
						const indexToRemove = cleanedItems.indexOf(itemToRemove);
						cleanedItems.splice(indexToRemove, 1);
					}
				}
			}

			let i = 0;
			let currentItem = cleanedItems[i];
			while (currentItem) {
				const previousItem = cleanedItems[i - 1],
					nextItem = cleanedItems[i + 1];

				let purgeCurrent = false;

				if (!nextItem) {
					// last item must be a closing bracket or metric
					if (!(currentItem.isMetric || currentItem.type === ')')) {
						purgeCurrent = true;
					}
				}
				if (!previousItem) {
					// first item must be an opening bracket or metric
					if (!(currentItem.isMetric || currentItem.type === '(')) {
						purgeCurrent = true;
					}
				} else {
					// if we have a metric followed by operator followed by closing bracket, remove the operator
					if (previousItem.isMetric && currentItem.isMath && nextItem?.type === ')') {
						purgeCurrent = true;
						// if previous is an opening bracket, current may not be an operator or closing bracket
					} else if (
						previousItem.type === '(' &&
						(currentItem.isMath || currentItem.type === ')')
					) {
						purgeCurrent = true;
						// if previous is a closing bracket, current may not be a metric or opening bracket
					} else if (
						previousItem.type === ')' &&
						(currentItem.isMetric || currentItem.type === '(')
					) {
						purgeCurrent = true;
						// if previous is an operator, current may not be a closing bracket or an operator
					} else if (
						previousItem.isMath &&
						(currentItem.isMath || currentItem.type === ')')
					) {
						purgeCurrent = true;
						// if previous is a metric, current may not be a metric or opening bracket
					} else if (
						previousItem.isMetric &&
						(currentItem.isMetric || currentItem.type === '(')
					) {
						purgeCurrent = true;
					}
				}
				if (purgeCurrent) {
					cleanedItems.splice(i, 1);
					isChanged = true;
				}
				i++;
				currentItem = cleanedItems[i];
			}
		} while (isChanged);

		return cleanedItems;
	}

	static getGroupOperators(items: ScriptItem[], groupStartIndex: number, groupEndIndex: number) {
		const operators: string[] = [];

		let bracketCounter = 0;
		for (let i = groupStartIndex; i <= groupEndIndex; i++) {
			const item = items[i];
			if (item) {
				if (item.type === ')') {
					bracketCounter--; // we need to be on 0 to count the operator
				} else if (item.type === '(') {
					bracketCounter++;
				} else if (!bracketCounter && item.isMath) {
					operators.push(item.type);
				}
			}
		}

		return operators;
	}
}
